import React, { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../protected/firebase";
import QRCode from "react-qr-code";
import { useAuth } from '../../../contexts/AuthContext';

export default function CustomerDisplay() {
  const {companyId} = useAuth();
  const [cart, setCart] = useState([]);
  const [view, setView] = useState("poster"); // poster | duitnow | receipt
  const [qrUrl, setQRUrl] = useState("");
  const [posterUrl, setPosterUrl] = useState("");
  const [duitnowQRUrl, setDuitnowQRUrl] = useState("");
  const [companyName, setCompanyName] = useState("");

  const [transactionInfo, setTransactionInfo] = useState({
    mode: "",
    companyName: "",
    subtotal: 0,
    tax: 0,
    discount: 0,
    rounding: 0,
    total: 0,
    change: 0,
    datetime: new Date(),
  });

useEffect(() => {
  const fetchCompanyAssets = async () => {
    if (!companyId) return;
    try {
      const infoRef = doc(db, "companies", companyId, "companySettings", "info");
      const infoSnap = await getDoc(infoRef);

      if (!infoSnap.exists()) {
        console.warn("⚠️ No company info found for:", companyId);
        return;
      }

      const data = infoSnap.data();

      setPosterUrl(data.posterUrl || data.poster || "");
      setDuitnowQRUrl(data.duitnowQRUrl || data.qrUrl || "");
      setView("poster");
    } catch (err) {
      console.error("❌ Error fetching company info:", err);
    }

    try {
      const infoRef = doc(db, "companies", companyId, "companyProfile", "info");
      const infoSnap = await getDoc(infoRef);

      if (!infoSnap.exists()) {
        console.warn("⚠️ No company info found for:", companyId);
        return;
      }

      const data = infoSnap.data();

      setCompanyName(data.companyName);
      setView("poster");
    } catch (err) {
      console.error("❌ Error fetching company info:", err);
    }
  };

  fetchCompanyAssets();
}, [companyId]);

  // 🔹 BroadcastChannel listener
  useEffect(() => {
    const bc = new BroadcastChannel("pos_display");

    bc.onmessage = (event) => {
      const { type, cart, qrUrl, info } = event.data;

      if (type === "UPDATE_CART") setCart(cart);
      if (type === "UPDATE_INFO") {
        setTransactionInfo((prev) => ({ ...prev, ...info }));
      }

      if (type === "SHOW_DUITNOW_QR") {
        setView("duitnow");
        setQRUrl(qrUrl || duitnowQRUrl); // use provided QR or default
      }

      if (type === "SHOW_RECEIPT_QR") {
        setView("receipt");
        setQRUrl(qrUrl);
      }

      if (type === "SHOW_POSTER") {
        setView("poster");
        setQRUrl("");
      }
    };

    const timer = setInterval(() => {
      setTransactionInfo((prev) => ({ ...prev, datetime: new Date() }));
    }, 1000);

    return () => {
      bc.close();
      clearInterval(timer);
    };
  }, [duitnowQRUrl]);

  const formatCurrency = (num) => `RM${parseFloat(num || 0).toFixed(2)}`;

return (
  <div className="fixed inset-0 w-screen h-screen bg-black text-white font-sans overflow-hidden flex">
      {/* Left: Cart + Summary */}
      <div className="w-1/2 flex flex-col justify-between border-r border-gray-800 p-6">
        <div>
          <h1 className="text-3xl font-extrabold text-purple-400 mb-1">
            {companyName || "Your Company"}
          </h1>
          <p className="text-sm text-gray-400">
            {transactionInfo.datetime.toLocaleString()}
          </p>
          <div className="mt-2 text-gray-300 text-sm">
            <p>
              Mode:{" "}
              <span className="font-medium capitalize">
                {transactionInfo.mode || "-"}
              </span>
            </p>
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 mt-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          <h2 className="text-xl font-semibold mb-2 border-b border-gray-700 pb-1">
            Your Order
          </h2>
          {cart.length === 0 ? (
            <p className="text-gray-500 mt-4 text-center">
              No items added yet...
            </p>
          ) : (
            <ul className="space-y-3">
              {cart.map((item, i) => {
                const isBundle = item.items && Array.isArray(item.items);
                return (
                  <li
                    key={`${item.id || i}-${item.variantId || ''}-${i}`}
                    className="flex justify-between items-center bg-gray-800/60 p-4 rounded-lg hover:bg-gray-700/60 transition text-base"
                  >
                    <div className="space-y-1">
                      <p className="font-semibold text-lg">
                        {item.name}
                        {isBundle && (
                          <span className="ml-2 text-[12px] bg-purple-600 text-white px-2 py-0.5 rounded-full">Bundle</span>
                        )}
                      </p>
                      {(() => {
                        const optionLabel = Array.isArray(item.selectedOptions)
                          ? item.selectedOptions
                              .map(
                                (o) =>
                                  `${o.optionLabel || o.optKey || ""}${o.optionLabel || o.optKey ? ": " : ""}${o.valueLabel || o.valueKey || ""}`
                              )
                              .filter(Boolean)
                              .join(" / ")
                          : "";
                        const label = item.variantName || optionLabel;
                        const deltaFromOptions = Array.isArray(item.selectedOptions)
                          ? item.selectedOptions.reduce((sum, o) => sum + Number(o.price || 0), 0)
                          : 0;
                        const delta =
                          Number(item.variantPriceDelta || 0) ||
                          deltaFromOptions;
                        if (!label) return null;
                        return (
                          <p className="text-sm text-gray-400">
                            {label}
                            {delta ? ` (+RM ${delta.toFixed(2)})` : ""}
                          </p>
                        );
                      })()}
                      <p className="text-sm text-gray-300">Qty: {item.qty}</p>
                      {isBundle && item.items?.length > 0 && (
                        <div className="mt-1 text-sm text-gray-400 space-y-0.5">
                          {item.items.map((bItem, idx) => {
                            const delta =
                              Number(bItem.variantPriceDelta || 0) ||
                              (Array.isArray(bItem.selectedOptions)
                                ? bItem.selectedOptions.reduce(
                                    (sum, o) => sum + Number(o.price || 0),
                                    0
                                  )
                                : 0);
                            const deltaText = delta ? ` (+RM ${delta.toFixed(2)})` : '';
                            return (
                              <div key={`${i}-b-${idx}`} className="flex justify-between gap-2">
                                <span className="truncate">
                                  • {bItem.name}
                                  {bItem.variantName ? ` (${bItem.variantName})` : ''}
                                  {deltaText}
                                </span>
                                <span className="whitespace-nowrap">x{bItem.quantity * (item.qty || 1)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <p className="text-gray-100 font-bold text-lg">
                      {formatCurrency(item.price * item.qty)}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Summary */}
        <div className="mt-4 pt-4 border-t border-gray-700 space-y-2 text-base">
          <div className="flex justify-between font-semibold">
            <span>Subtotal</span>
            <span>{formatCurrency(transactionInfo.subtotal)}</span>
          </div>
          {transactionInfo.tax > 0 && (
            <div className="flex justify-between font-semibold">
              <span>Tax</span>
              <span>{formatCurrency(transactionInfo.tax)}</span>
            </div>
          )}
          {transactionInfo.discount > 0 && (
            <div className="flex justify-between text-green-400 font-semibold">
              <span>Discount</span>
              <span>-{formatCurrency(transactionInfo.discount)}</span>
            </div>
          )}
          {transactionInfo.rounding !== 0 && (
            <div className="flex justify-between font-semibold">
              <span>Rounding</span>
              <span>{formatCurrency(transactionInfo.rounding)}</span>
            </div>
          )}
          <div className="flex justify-between text-2xl font-extrabold mt-2 border-t border-gray-700 pt-2">
            <span>Total</span>
            <span>{formatCurrency(transactionInfo.total)}</span>
          </div>
          {transactionInfo.change > 0 && (
            <div className="flex justify-between text-yellow-400 font-bold text-lg">
              <span>Change</span>
              <span>{formatCurrency(transactionInfo.change)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Right: Poster or QR Display */}
<div className="w-1/2 h-full flex items-center justify-center bg-gray-900 overflow-hidden">
        {view === "poster" && (
          posterUrl ? (
            <img
              src={posterUrl}
              alt="Poster"
              className="w-full h-full object-cover rounded-lg"
            />
          ) : (
            <h1 className="text-3xl font-bold text-gray-400">
              Welcome to {companyName || "Your Company"}
            </h1>
          )
        )}

        {view === "duitnow" && (
          <div className="flex flex-col items-center justify-center text-center space-y-4">
            <h2 className="text-2xl font-semibold text-pink-400">
              Scan to Pay (DuitNow)
            </h2>
            <div className="bg-white p-4 rounded-xl shadow-lg">
              <img
                src={qrUrl || duitnowQRUrl}
                alt="DuitNow QR"
                className="max-w-full max-h-full object-contain"
              />
            </div>
            <p className="text-gray-400 text-sm mt-2">
              Secure payment powered by DuitNow
            </p>
          </div>
        )}

{view === "receipt" && (
  <div className="flex flex-col items-center justify-center text-center space-y-4 w-full h-full">
    <h2 className="text-3xl font-semibold text-green-400">Receipt QR</h2>
    <div className="flex items-center justify-center bg-white p-6 rounded-2xl shadow-xl w-[50vmin] h-[50vmin]">
      <QRCode
        id="receiptQrCanvas"
        value={qrUrl || ""}
        size={0} // this makes it use CSS size instead
        style={{ width: "100%", height: "100%" }}
      />
    </div>
    <p className="text-gray-400 text-sm mt-2">Scan to view your e-receipt</p>
  </div>
)}
      </div>
    </div>
  );
}
